import React from 'react';
import { Icon } from '@iconify/react';
import { formatIntDateTime } from '../utils/dateUtils';

const formatDelayDuration = (delay) => {
  if (!delay?.delayStartDate) {
    return '';
  }

  const start = new Date(delay.delayStartDate);
  const end = new Date(delay.delayEndDate || new Date());
  if (Number.isNaN(start.getTime()) || Number.isNaN(end.getTime())) {
    return '';
  }

  const totalMinutes = Math.max(0, Math.round((end - start) / (1000 * 60)));
  const hours = Math.floor(totalMinutes / 60);
  const minutes = totalMinutes % 60;

  const parts = [];
  if (hours) parts.push(`${hours}h`);
  if (minutes) parts.push(`${minutes}m`);
  if (parts.length === 0) {
    parts.push('0m');
  }

  return parts.join(' ');
};

const formatDelayList = (delays = []) => {
  if (!Array.isArray(delays) || delays.length === 0) {
    return '';
  }

  return delays
    .map((delay, index) => {
      const label =
        delay?.eventDelayCode?.name || `Delay ${index + 1}`;
      return label;
    })
    .join(', ');
};

const formatDownHours = (startDate) => {
  if (!startDate) {
    return '';
  }

  const start = new Date(startDate);
  if (Number.isNaN(start.getTime())) {
    return '';
  }

  const totalMinutes = Math.max(0, Math.round((Date.now() - start.getTime()) / (1000 * 60)));
  const hours = Math.floor(totalMinutes / 60);
  const minutes = totalMinutes % 60;

  if (minutes === 0) {
    return `${hours}h`;
  }

  return `${hours}h ${minutes}m`;
};

const EventDataTable = ({
  events = [],
  onEdit,
  onDelete,
  onShowComments,
  onShowDelays,
  loading,
  sortField,
  sortOrder,
  onSort,
  hiddenColumns = [],
  activeTab = 'planned',
}) => {
  const isHidden = (key) => hiddenColumns.includes(key);
  const tableEvents = React.useMemo(() => {
    if (sortField !== 'eventType' || sortOrder !== 'asc') {
      return events;
    }

    return [...events].sort((a, b) => {
      const typeNameA = (a?.eventType?.name || '').toLowerCase();
      const typeNameB = (b?.eventType?.name || '').toLowerCase();
      const typeCompare = typeNameA.localeCompare(typeNameB);
      if (typeCompare !== 0) {
        return typeCompare;
      }

      const startA = new Date(a?.startDate || 0).getTime() || 0;
      const startB = new Date(b?.startDate || 0).getTime() || 0;
      return startA - startB;
    });
  }, [events, sortField, sortOrder]);

  const columnOrder = React.useMemo(() => {
    if (activeTab === 'all') {
      return [
        { key: 'plannedStart', label: 'Planned Start', sortField: 'plannedStart' },
        { key: 'plannedEnd', label: 'Planned End', sortField: 'plannedEnd' },
        { key: 'start', label: 'Start Date', sortField: 'start' },
        { key: 'end', label: 'End Date', sortField: 'end' },
        { key: 'delays', label: 'Delays', sortable: false },
      ];
    }

    if (activeTab === 'inProgress') {
      return [
        { key: 'plannedStart', label: 'Planned Start', sortField: 'plannedStart' },
        { key: 'start', label: 'Start Date', sortField: 'start' },
        { key: 'plannedEnd', label: 'Estimated End', sortField: 'plannedEnd' },
        { key: 'downHours', label: 'Down Hours', sortable: false },
        { key: 'delays', label: 'Delays', sortable: false },
        { key: 'end', label: 'End Date', sortField: 'end' },
      ];
    }

    if (activeTab === 'done') {
      return [
        { key: 'plannedStart', label: 'Planned Start', sortField: 'plannedStart' },
        { key: 'plannedEnd', label: 'Planned End', sortField: 'plannedEnd' },
        { key: 'start', label: 'Start Date', sortField: 'start' },
        { key: 'end', label: 'End Date', sortField: 'end' },
        { key: 'delays', label: 'Delays', sortable: false },
      ];
    }

    return [
      { key: 'plannedStart', label: 'Planned Start', sortField: 'plannedStart' },
      { key: 'plannedEnd', label: 'Planned End', sortField: 'plannedEnd' },
      { key: 'start', label: 'Start Date', sortField: 'start' },
      { key: 'end', label: 'End Date', sortField: 'end' },
    ];
  }, [activeTab]);
  const columnKeys = [
    'name',
    ...(activeTab === 'all' ? ['status'] : []),
    'asset',
    'eventLocation',
    ...columnOrder.map((column) => column.key),
    ...(activeTab === 'all' ? [] : []),
  ];
  const visibleColumnCount = 2 + columnKeys.filter((key) => !isHidden(key)).length;

  const getStatusName = (row) =>
    row?.eventStatus2?.name || row?.eventStatus?.name || '';

  const getCommentCount = (row) => {
    const comments = row?.tblEventComments || row?.eventComments || [];
    return Array.isArray(comments) ? comments.length : 0;
  };

  const getLatestCommentPreview = (row) => {
    const comments = row?.tblEventComments || row?.eventComments || [];
    if (!Array.isArray(comments) || comments.length === 0) return '';

    const latest = [...comments].sort((a, b) => {
      const aTime = new Date(a?.createdAt || a?.created_at || 0).getTime() || 0;
      const bTime = new Date(b?.createdAt || b?.created_at || 0).getTime() || 0;
      return bTime - aTime;
    })[0];

    const text = String(latest?.comments || latest?.comment || '').trim();
    if (!text) return '';
    return text.length > 100 ? `${text.slice(0, 100)}...` : text;
  };

  const getDelayCount = (row) => {
    const delays = row?.tblEventDelays || row?.eventDelays || [];
    return Array.isArray(delays) ? delays.length : 0;
  };

  const getOpenDelayCodes = (row) => {
    const delays = row?.tblEventDelays || row?.eventDelays || [];
    if (!Array.isArray(delays) || delays.length === 0) return [];
    return delays
      .filter((delay) => delay?.delayStartDate && !delay?.delayEndDate)
      .map((delay) => delay?.eventDelayCode?.name)
      .filter(Boolean);
  };

  const getResolvedDelayCodes = (row) => {
    const delays = row?.tblEventDelays || row?.eventDelays || [];
    if (!Array.isArray(delays) || delays.length === 0) return [];

    return delays
      .filter(
        (delay) =>
          delay?.delayStartDate &&
          delay?.delayEndDate
      )
      .map(
        (delay) =>
          delay?.eventDelayCode?.name
      )
      .filter(Boolean);
  };

  const getSortIcon = (field) => {
    if (sortField !== field || !sortOrder) {
      return 'mdi:sort';
    }
    return sortOrder === 'asc' ? 'mdi:sort-ascending' : 'mdi:sort-descending';
  };

  const renderSortableHeader = (label, field, key) => {
    const ariaSort =
      sortField === field
        ? sortOrder === 'asc'
          ? 'ascending'
          : sortOrder === 'desc'
            ? 'descending'
            : 'none'
        : 'none';

    return (
      <th
        key={key}
        onClick={() => onSort?.(field)}
        style={{ cursor: 'pointer' }}
        scope="col"
        aria-sort={ariaSort}
      >
        <span className="d-inline-flex align-items-center gap-1">
          {label}
          <Icon className="text-xl" icon={getSortIcon(field)} />
        </span>
      </th>
    );
  };

  return (
    <table className="table bordered-table mb-3 w-100 rounded-0">
      <thead>
        <tr>
          {renderSortableHeader('Type', 'eventType', 'eventType')}
          {!isHidden('asset') && renderSortableHeader('Asset', 'asset', 'asset')}
          {!isHidden('name') && renderSortableHeader('Name', 'name', 'name')}
          {activeTab === 'all' &&
            !isHidden('status') &&
            renderSortableHeader('Status', 'status', 'status')}
          {!isHidden('department') && renderSortableHeader('Department', 'department', 'department')}          
          {/*{!isHidden('eventLocation') &&
            renderSortableHeader('Event Location', 'eventLocation', 'eventLocation')}*/}
          {columnOrder.map((column) => {
            if (isHidden(column.key)) {
              return null;
            }

            if (column.sortable === false || !column.sortField) {
              return (
                <th key={column.key} scope="col">
                  {column.label}
                </th>
              );
            }

            return renderSortableHeader(column.label, column.sortField, column.key);
          })}
          {!isHidden('comments') && <th scope="col">Comments</th>}
          <th className="rounded-0">Actions</th>
        </tr>
      </thead>
      <tbody>
        {events.length === 0 && !loading && (
          <tr>
            <td colSpan={visibleColumnCount} className="text-center py-4 rounded-0">No Events Loaded</td>
          </tr>
        )}
        {tableEvents.map((row) => (
          <tr key={row.id}>
            <td>
              <div
                className={`d-inline-flex align-items-center justify-content-center 
                rounded-circle bg-${row.eventType.iconColor}-focus text-white`}
                style={{ width: "40px", height: "40px" }}
              >
                <Icon
                  icon={`mdi:${row.eventType.eventAppIcon}`}
                  className={`fs-5 text-${row.eventType.iconColor}`} 
                />
              </div>
            </td>
            {!isHidden('asset') && (
              <td>
                <div>{row.asset ? `${row.asset.assetNumber || ''}` : ''}</div>
                <div className="text-sm text-secondary">{row.asset ? `${row.asset.assetDescription || ''}` : ''}</div>
              </td>
            )}
            {!isHidden('name') && <td className="fw-semibold">{row.name}</td>}
            {activeTab === 'all' && !isHidden('status') && (
              <td className="fw-semibold">{getStatusName(row)}</td>
            )}
            {!isHidden('department') && (
              <td>{row.asset.assetDepartment ? `${row.asset.assetDepartment.name}` : ''}</td>
            )}
            {/*{!isHidden('eventLocation') && <td>{row.eventLocation?.name || ''}</td>}*/}
            {columnOrder.map(({ key }) => {
              if (isHidden(key)) {
                return null;
              }

              if (key === 'plannedStart') {
                return <td key={`${row.id}-${key}`}>{formatIntDateTime(row.plannedStartDate)}</td>;
              }

              if (key === 'plannedEnd') {
                return <td key={`${row.id}-${key}`}>{formatIntDateTime(row.plannedEndDate)}</td>;
              }

              if (key === 'start') {
                return <td key={`${row.id}-${key}`}>{formatIntDateTime(row.startDate)}</td>;
              }

              if (key === 'end') {
                return <td  key={`${row.id}-${key}`}>{formatIntDateTime(row.endDate)}</td>;
              }

              if (key === 'downHours') {
                return <td key={`${row.id}-${key}`}>{formatDownHours(row.startDate)}</td>;
              }

              if (key === 'delays') {
                const delays = row.tblEventDelays || row.eventDelays || [];
                const openDelayCodes = getOpenDelayCodes(row);
                const resolvedDelayCodes = getResolvedDelayCodes(row);
                return (
                  <td key={`${row.id}-${key}`} className="text-left">
                    {delays?.length > 0 ? (
                      <div className="d-flex flex-wrap align-items-center gap-2">
                        {/* Active chips */}
                        {openDelayCodes?.length > 0 && (
                          <>
                            {openDelayCodes.map((code) => (
                              <span
                                key={`active-${code}`}
                                className="badge rounded-pill bg-danger-focus text-danger border border-danger-subtle"
                                style={{ whiteSpace: 'normal' }}
                                title="Active delay"
                              >
                                {code}
                              </span>
                            ))}
                          </>
                        )}

                        {/* Resolved chips */}
                        {resolvedDelayCodes?.length > 0 && (
                          <>
                            {resolvedDelayCodes.map((code) => (
                              <span
                                key={`resolved-${code}`}
                                className="badge rounded-pill bg-light text-muted border"
                                style={{ whiteSpace: 'normal' }}
                                title="Resolved delay"
                              >
                                {code}
                              </span>
                            ))}
                          </>
                        )}
                      </div>
                    ) : (
                      ''
                    )}
                  </td>
                );
              }

              return null;
            })}
            {!isHidden('comments') && (
              <td className="text-left" style={{ width: "13%" }}>
                {getCommentCount(row) > 0 && (
                  <div className="d-flex align-items-start gap-2">
                    <span className="text-wrap text-sm">
                      {getLatestCommentPreview(row)}
                    </span>
                  </div>
                )}
              </td>
            )}
            <td style={{ width: "100px" }}>
              <button
                className="edit-btn w-32-px h-32-px bg-success-focus text-success-main rounded-circle d-inline-flex align-items-center justify-content-center mx-6"
                onClick={() => onEdit(row)}
              >
                <Icon icon="ic:baseline-edit" />
              </button>
              <button
                className="delete-btn w-32-px h-32-px bg-danger-focus text-danger-main rounded-circle d-inline-flex align-items-center justify-content-center"
                onClick={() => onDelete(row.id)}
              >
                <Icon icon="ic:baseline-delete-outline" />
              </button>
            </td>
          </tr>
        ))}
        {loading && (
          <tr>
            <td colSpan={visibleColumnCount} className="text-center py-2">
              <span className="spinner-border spinner-border-sm" />
            </td>
          </tr>
        )}
      </tbody>
    </table>
  );
};

export default EventDataTable;
